import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Globe, 
  Smartphone, 
  Users, 
  BarChart3, 
  Zap, 
  CheckCircle2,
  ArrowRight,
  Dumbbell,
  Scissors,
  Coffee,
  Building2,
  GraduationCap,
  ShoppingBag
} from 'lucide-react';

const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_biztech-hub-3/artifacts/m87hwkqd_9.png';

const services = [
  {
    icon: Globe,
    title: 'Website Development',
    description: 'Custom, responsive websites that convert visitors into customers.',
  },
  {
    icon: Smartphone,
    title: 'Digital Presence',
    description: 'Establish your brand across all digital platforms effectively.',
  },
  {
    icon: Users,
    title: 'Causaler Hire',
    description: 'Pool worker hiring platform for flexible staffing solutions.',
  },
  {
    icon: BarChart3,
    title: 'Business Dashboards',
    description: 'Data-driven insights to make smarter business decisions.',
  },
];

const industries = [
  { icon: Dumbbell, name: 'Gyms', image: 'https://images.unsplash.com/photo-1738523686619-1b3b5d2405ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=400' },
  { icon: Scissors, name: 'Salons', image: 'https://images.unsplash.com/photo-1511920922889-5c35bfd95a7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=400' },
  { icon: Coffee, name: 'Cafes', image: 'https://images.unsplash.com/photo-1678449509379-a5afe8e85429?crop=entropy&cs=srgb&fm=jpg&q=85&w=400' },
  { icon: GraduationCap, name: 'Coaching' },
  { icon: ShoppingBag, name: 'Retail Shops' },
  { icon: Building2, name: 'Local Services' },
];

const whyUs = [
  { title: 'Simple', description: 'No technical complexity. We handle everything for you.' },
  { title: 'Affordable', description: 'Solutions designed for small business budgets.' },
  { title: 'Scalable', description: 'Grow your digital presence as your business grows.' },
];

export default function HomePage() {
  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-subtle py-20 md:py-32" data-testid="hero-section">
        <div className="hero-pattern absolute inset-0 opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-block px-4 py-2 bg-orange-100 text-[#F5941E] rounded-full text-sm font-semibold mb-6">
                Technology for Small Business Growth
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] leading-tight mb-6">
                Grow Your Business{' '}
                <span className="text-gradient">Digitally</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
                Causaler empowers gyms, salons, cafes and local businesses with simple, 
                scalable and affordable tech solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    className="bg-[#F5941E] hover:bg-[#D97F12] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105"
                    data-testid="hero-cta-primary"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    variant="outline"
                    className="rounded-full px-8 py-6 text-lg font-semibold border-2 border-slate-200 hover:border-[#1E5E9F] hover:text-[#1E5E9F] transition-all duration-300"
                    data-testid="hero-cta-secondary"
                  >
                    Explore Services
                  </Button>
                </Link>
              </div>
            </div>
            <div className="animate-slide-in-right delay-200 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-200/50 to-blue-200/50 rounded-3xl blur-2xl" />
                <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
                  <div className="flex items-center gap-4 mb-6">
                    <img src={LOGO_ICON} alt="" className="w-12 h-12" />
                    <div>
                      <h3 className="font-heading font-bold text-lg">Causaler Platform</h3>
                      <p className="text-sm text-slate-500">Your Business Command Center</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <span className="text-slate-600">Website Visitors</span>
                      <span className="font-bold text-[#1E5E9F]">+127%</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <span className="text-slate-600">New Customers</span>
                      <span className="font-bold text-[#F5941E]">+84</span>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl">
                      <span className="text-slate-600">Revenue Growth</span>
                      <span className="font-bold text-green-600">+43%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      {/* <section className="py-12 border-b border-slate-100" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-8 font-medium">
            Trusted by 500+ local businesses across India
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60">
            {['Fitness Hub', 'Style Studio', 'Cafe Central', 'Learn Academy', 'Local Mart'].map((brand) => (
              <span key={brand} className="text-slate-400 font-heading font-semibold text-lg">
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section> */}

      {/* Services Section */}
      <section className="py-20 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#F5941E] font-semibold text-sm tracking-wider uppercase">
              Our Services
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
              Everything You Need to{' '}
              <span className="text-[#1E5E9F]">Go Digital</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Comprehensive solutions designed specifically for businesses
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card 
                key={service.title}
                className="group bg-white p-6 rounded-3xl border border-slate-100 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-0">
                  <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mb-6 group-hover:bg-[#F5941E] transition-colors duration-300">
                    <service.icon className="w-7 h-7 text-[#F5941E] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl mb-3">{service.title}</h3>
                  <p className="text-slate-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                variant="outline" 
                className="rounded-full px-8 py-3 font-semibold border-2 border-[#1E5E9F]/20 hover:border-[#1E5E9F] text-[#1E5E9F] transition-all duration-300"
                data-testid="services-view-all"
              >
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Product Spotlight - Causaler Hire */}
      <section className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="product-spotlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-blue-100 text-[#1E5E9F] rounded-full text-sm font-semibold mb-6">
                Launching Soon
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                <span className="text-[#F5941E]">Causaler</span> Hire
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                The smart pool worker hiring platform that connects businesses with flexible, 
                reliable workforce on-demand. Perfect for seasonal peaks, events or daily operations.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Access verified workers instantly',
                  'Flexible hiring - hourly, daily or long-term',
                  'Simple scheduling and payment',
                  'Built for gyms, cafes, retail and more',
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#F5941E]" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
              {/* <Link to="/product">
                <Button 
                  className="bg-[#1E5E9F] hover:bg-[#164B82] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transition-all duration-300"
                  data-testid="product-cta"
                >
                  Learn More About Hire
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link> */}
            </div>
            <div className="lg:pl-8">
              <div className="mockup-browser">
                <div className="mockup-browser-header">
                  <span className="mockup-browser-dot bg-red-400" />
                  <span className="mockup-browser-dot bg-yellow-400" />
                  <span className="mockup-browser-dot bg-green-400" />
                </div>
                <div className="mockup-browser-content">
                  <div className="flex items-center justify-between mb-4 pb-4 border-b">
                    <div className="flex items-center gap-3">
                      <img src={LOGO_ICON} alt="" className="w-8 h-8" />
                      <span className="font-heading font-bold text-[#1E5E9F]">Causaler Hire</span>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Coming Soon</span>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded-lg border flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-[#F5941E]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">Available Workers</p>
                          <p className="text-xs text-slate-500">Ready to hire today</p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-[#F5941E]">124</span>
                    </div>
                    <div className="bg-white p-3 rounded-lg border flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Zap className="w-5 h-5 text-[#1E5E9F]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">Quick Hire</p>
                          <p className="text-xs text-slate-500">Get staff in minutes</p>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 md:py-32" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#F5941E] font-semibold text-sm tracking-wider uppercase">
              Industries We Serve
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
              Built for <span className="text-[#1E5E9F]">Local Businesses</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              From gyms to cafes, we understand the unique needs of small businesses
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {industries.map((industry, index) => (
              <div 
                key={industry.name}
                className="group relative bg-white rounded-2xl p-6 border border-slate-100 hover:border-[#F5941E]/30 hover:shadow-lg transition-all duration-300 text-center"
                data-testid={`industry-${index}`}
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-slate-50 group-hover:bg-orange-50 flex items-center justify-center mb-4 transition-colors duration-300">
                  <industry.icon className="w-7 h-7 text-slate-600 group-hover:text-[#F5941E] transition-colors duration-300" />
                </div>
                <h3 className="font-heading font-semibold text-slate-800">{industry.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Causaler */}
      <section className="py-20 md:py-32 bg-[#0F172A]" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#F5941E] font-semibold text-sm tracking-wider uppercase">
              Why Choose Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6 text-white">
              Why <span className="text-[#F5941E]">Causaler</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyUs.map((item, index) => (
              <div 
                key={item.title}
                className="text-center p-8 rounded-3xl bg-slate-800/50 border border-slate-700/50 hover:border-[#F5941E]/30 transition-all duration-300"
                data-testid={`why-card-${index}`}
              >
                <span className="inline-block w-16 h-16 rounded-2xl bg-[#F5941E]/10 text-[#F5941E] font-heading font-bold text-3xl flex items-center justify-center mb-6 mx-auto">
                  {index + 1}
                </span>
                <h3 className="font-heading text-2xl font-bold text-white mb-4">{item.title}</h3>
                <p className="text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-brand" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
            Join hundreds of small businesses already transforming their digital presence with Causaler.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#contact-form">
              <Button 
                className="bg-white text-[#F5941E] hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
                data-testid="cta-primary"
              >
                Start Free Consultation
              </Button>
            </Link>
            <Link to="/services">
              <Button 
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
                data-testid="cta-secondary"
              >
                View Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
